import { DestroyRef, Injectable, OnDestroy } from '@angular/core';
import { QueryRef } from 'apollo-angular';
import { DebugableService } from './debug/debug.type';

export enum SyncEnum {
  FLUX = 'flux',
  FLUX_SETTINGS = 'flux-settings',
  STAGE = 'stage',
  WORKFLOW = 'workflow',
  STAGES_SETTINGS = 'stages-settings',
  MY_CARDS = 'my-cards',
  STAGES_CARDS_COUNT = 'stages-cards-count',
  NOTIFICATIONS = 'notifications',
  USER_NOTIFICATIONS = 'user-notifications',
  NOTIFICATIONS_COUNT = 'notifications-count',
  MY_REVIEWS = 'my-reviews',
}

export type SyncType = `${SyncEnum}`;

@Injectable({
  providedIn: 'root',
})
export class SyncService implements DebugableService, OnDestroy {
  public queriesRef: Map<string, { queryRef: QueryRef<any>; destroyRef: DestroyRef }> = new Map();

  public ngOnDestroy(): void {
    this.clearQueriesRef();
  }

  public setQueryRef<T = QueryRef<any>>({
    context,
    key,
    queryRef,
    destroyRef,
  }: {
    context: SyncType;
    key: string;
    queryRef: T;
    destroyRef: DestroyRef;
  }) {
    if (this.queriesRef.has(`${context}-${key}`)) {
      this.removeQueryRef(context, key);
    }

    this.queriesRef.set(`${context}-${key}`, { queryRef: queryRef as QueryRef<any>, destroyRef });

    destroyRef.onDestroy(() => {
      this.removeQueryRef(context, key);
    });
  }

  public getQueryRef<T = QueryRef<any>>(context: SyncType, key: string): T {
    const result = this.queriesRef.get(`${context}-${key}`);

    if (!result) {
      throw new Error(`QueryRef not found for context: ${context}, key: ${key}`);
    }

    return result.queryRef as T;
  }

  public removeQueryRef(context: SyncType, key: string) {
    const queryRef = this.queriesRef.get(`${context}-${key}`);
    if (queryRef) {
      queryRef.queryRef.stopPolling();
    }

    this.queriesRef.delete(`${context}-${key}`);
  }

  public refreshQueryRef(context: SyncType, key: string) {
    const queryRef = this.queriesRef.get(`${context}-${key}`);
    if (queryRef) {
      queryRef.queryRef.refetch();
    }
  }

  public clearQueriesRef() {
    for (const queryRef of this.queriesRef.values()) {
      queryRef.queryRef.stopPolling();
    }

    this.queriesRef.clear();
  }

  public generateDebugReport() {
    return {
      queriesRef: this.queriesRef,
    };
  }
}
